import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle, Mail, Phone, MessageSquare } from 'lucide-react';
import { trackContactSubmit } from '../utils/analytics';

const API = `${window.location.origin}/api`;

/**
 * ContactForm — public-facing form on /about#contact.
 * Submissions land in the admin → Inbox tab.
 */
export default function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      trackContactSubmit();
      toast.success(res.data?.message || 'Message sent — we\'ll be in touch.');
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not send your message. Please try again.';
      toast.error(detail);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="contact-form-success" className="bg-[#141414] border border-[#1C7D3F]/30 p-8 text-center max-w-xl mx-auto">
        <CheckCircle className="w-10 h-10 text-[#1C7D3F] mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="text-2xl text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Thank you, {form.name}.</h3>
        <p className="text-white/50 text-sm font-sans mb-5">
          Your message is in our inbox. We reply within 1 business day — usually faster.
        </p>
        <button onClick={() => { setForm({ name: '', email: '', phone: '', subject: '', message: '' }); setSubmitted(false); }}
          className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-sans hover:text-[#F3E5AB]">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="bg-[#141414] border border-white/5 p-6 sm:p-8 max-w-xl mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <MessageSquare className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
        <h3 className="text-2xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Send us a message</h3>
      </div>
      <p className="text-white/50 text-sm font-sans mb-6">
        Questions about a fragrance? Order issue? Wholesale inquiry? Drop us a note — we reply within 1 business day.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Name *</label>
          <input
            type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            required maxLength={120}
            className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
            placeholder="Juan Dela Cruz"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Email *</label>
          <input
            type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            required
            className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
            placeholder="you@email.com"
            data-testid="contact-email-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Phone <span className="text-white/30">(optional)</span></label>
          <input
            type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
            placeholder="09XX XXX XXXX"
            data-testid="contact-phone-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Subject <span className="text-white/30">(optional)</span></label>
          <input
            type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
            className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
            placeholder="Order #, fragrance question, wholesale…"
            data-testid="contact-subject-input"
          />
        </div>
      </div>

      <div className="mb-5">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Message *</label>
        <textarea
          value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
          required maxLength={5000} rows={5}
          className="w-full bg-transparent border border-white/15 text-white text-sm p-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20 resize-y"
          placeholder="Tell us what's on your mind…"
          data-testid="contact-message-input"
        />
        <p className="text-[10px] text-white/30 font-sans mt-1.5">{form.message.length} / 5000 characters</p>
      </div>

      <button
        type="submit" disabled={submitting}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans font-semibold hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
        data-testid="contact-submit-btn"
      >
        <Send className="w-3.5 h-3.5" />
        {submitting ? 'Sending…' : 'Send Message'}
      </button>

      <div className="mt-6 pt-5 border-t border-white/5 flex flex-col gap-3 text-xs font-sans">
        <a href="mailto:support@jsfragrances.com" className="flex items-center gap-2 text-white/40 hover:text-[#D4AF37]">
          <Mail className="w-3.5 h-3.5" /> support@jsfragrances.com
        </a>
        <a href="https://www.facebook.com/profile.php?id=61588041885547" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/40 hover:text-[#1877F2]" data-testid="contact-fb-link">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
          Message us on Facebook
        </a>
        <a href="https://www.tiktok.com/@jsfragranceofficial" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/40 hover:text-white" data-testid="contact-tiktok-link">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.3a8.16 8.16 0 0 0 4.77 1.5v-3.43a4.85 4.85 0 0 1-1.84-.68z"/></svg>
          Follow @jsfragranceofficial on TikTok
        </a>
        <span className="flex items-center gap-2 text-white/40"><Phone className="w-3.5 h-3.5" /> Reply within 1 business day</span>
      </div>
    </form>
  );
}
